import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { AlterationRequestPayload, DirectAlteration } from '@app/features/alteration-requests';
import { Reservation } from '@app/shared/interfaces';
import { ReservationService } from '@app/shared/services/reservation/reservation.service';
import {
  AlertComponent,
  ButtonComponent,
  CurrencyCodePipe,
  DatePickerComponent,
  FormFieldComponent,
  LoadingSpinnerComponent,
  NumNightsPipe,
} from '@ui';
import { addDays, subDays } from 'date-fns';
import { Subject, debounceTime } from 'rxjs';

@Component({
  standalone: true,
  selector: 'sbnb-direct-alteration-request-reservation-details',
  templateUrl: './direct-alteration-request-reservation-details.component.html',
  styleUrls: ['./direct-alteration-request-reservation-details.component.scss'],
  imports: [
    DatePickerComponent,
    MatLegacyFormFieldModule,
    NumNightsPipe,
    DatePipe,
    LoadingSpinnerComponent,
    MatLegacyTableModule,
    MatLegacyInputModule,
    FormsModule,
    CurrencyCodePipe,
    FormFieldComponent,
    ButtonComponent,
    AlertComponent,
  ],
})
export class DirectAlterationRequestReservationDetailsComponent {
  private readonly reservationsService = inject(ReservationService);
  private readonly fetchQuoteSubject = new Subject<void>();

  readonly reservation = input.required<Reservation>();

  readonly fetchingQuote = input<boolean>(false);
  readonly fetchQuote = output<void>();

  readonly quoteError = input<string | null>(null);

  readonly reservations = signal<{ start: string; end: string }[] | null>(null);

  readonly alterationDetails = model<DirectAlteration | null>(null);
  readonly alterationRequest = model<AlterationRequestPayload>();

  private readonly hasCalledFetchReservations = signal(false);

  readonly propertyId = computed(() => this.alterationDetails()?.property.id);
  readonly reservationCode = computed(() => this.alterationDetails()?.reservation_code);

  readonly datesChanged = computed(() => {
    return (
      this.alterationRequest()?.start_date !== this.alterationDetails()?.previous_state.checkin ||
      this.alterationRequest()?.end_date !== this.alterationDetails()?.previous_state.checkout
    );
  });

  readonly adultsChanged = computed(() => {
    return this.alterationRequest()?.number_of_adults !== this.alterationDetails()?.previous_state.num_guests.adults;
  });

  readonly childrenChanged = computed(() => {
    return (
      this.alterationRequest()?.number_of_children !== this.alterationDetails()?.previous_state.num_guests.children
    );
  });

  readonly infantsChanged = computed(() => {
    return this.alterationRequest()?.number_of_infants !== this.alterationDetails()?.previous_state.num_guests.infants;
  });

  readonly petsChanged = computed(() => {
    return this.alterationRequest()?.number_of_pets !== this.alterationDetails()?.previous_state.num_guests.pets;
  });

  readonly displayedColumns = signal<string[]>(['item', 'old', 'new']);
  readonly removedItems = signal<Set<string>>(new Set());

  readonly priceBreakdownLines = computed(() => {
    const alteration = this.alterationDetails();
    if (!alteration) return [];

    const previousLines = alteration.previous_state.lines || [];
    const currentLines = alteration.current_state.lines || [];
    const removedItems = this.removedItems();

    // Create a map of current line items by title for easy lookup
    const currentLinesMap = new Map(currentLines.map((line) => [line.title, line]));

    return previousLines
      .filter((previousLine) => {
        const currentLine = currentLinesMap.get(previousLine.title);
        const key = currentLine?.metadata.pricing_key;
        return !removedItems.has(key || '');
      })
      .map((previousLine) => {
        const currentLine = currentLinesMap.get(previousLine.title);

        return {
          title: previousLine.title,
          old: previousLine.value.formatted_string,
          new: currentLine?.value.formatted_string || '—',
          newRawValue: currentLine?.value.formatted_decimal || 0,
          key: currentLine?.metadata.pricing_key,
          canEdit: currentLine?.metadata.capabilities.can_edit,
          canRemove: currentLine?.metadata.capabilities.can_remove,
          isRemoved: removedItems.has(currentLine?.metadata.pricing_key || ''),
        };
      })
      .filter((line) => {
        // Filter out lines where both old and new values are effectively zero
        const oldValue = previousLines.find((p) => p.title === line.title)?.value.amount || '0';
        const newValue = currentLinesMap.get(line.title)?.value.amount || '0';
        return oldValue !== '0' || newValue !== '0';
      });
  });

  constructor() {
    // Only call fetchReservations the first time alterationDetails has a value
    effect(
      () => {
        const alterationDetails = this.alterationDetails();
        const hasCalledFetch = this.hasCalledFetchReservations();

        if (alterationDetails && !hasCalledFetch) {
          this.fetchReservations();
          this.hasCalledFetchReservations.set(true);
        }
      },
      { allowSignalWrites: true }
    );

    // Set up debounced fetchQuote emission
    this.fetchQuoteSubject.pipe(debounceTime(500), takeUntilDestroyed()).subscribe(() => {
      this.fetchQuote.emit();
    });
  }

  fetchReservations() {
    const propertyId = this.propertyId();
    const reservationCode = this.reservationCode();

    if (!propertyId || !reservationCode) return;

    this.reservationsService
      .getReservationsByDate(subDays(new Date(), 90), addDays(new Date(), 1000), [propertyId], false, true)
      .subscribe((res) => {
        this.reservations.set(
          res
            .filter((item) => item.code !== reservationCode)
            .map((item) => {
              return { start: item.checkin, end: item.checkout };
            })
        );
      });
  }

  datesSelected(dates: [string, string]) {
    if (dates[0] === this.alterationRequest().start_date && dates[1] === this.alterationRequest().end_date) {
      return;
    }

    this.alterationRequest.update((alterationRequest) => {
      if (!alterationRequest) return alterationRequest;

      return {
        ...alterationRequest,
        start_date: dates[0],
        end_date: dates[1],
      };
    });

    this.#clearCustomPricing();
    this.fetchQuoteSubject.next();
  }

  updateGuestCount({
    key,
    value,
  }: {
    key: 'number_of_adults' | 'number_of_children' | 'number_of_infants' | 'number_of_pets';
    value: string;
  }) {
    this.alterationRequest.update((alterationRequest) => {
      if (!alterationRequest) return alterationRequest;

      return {
        ...alterationRequest,
        [key]: +value,
      };
    });

    this.#clearCustomPricing();
    this.fetchQuoteSubject.next();
  }

  removeLine(key: string) {
    if (!key) return;

    // Add to removed items set
    this.removedItems.update((removed) => {
      const newSet = new Set(removed);
      newSet.add(key);
      return newSet;
    });

    // Update the alteration request to set the pricing to 0 for this item
    this.alterationRequest.update((alterationRequest) => {
      if (!alterationRequest) return alterationRequest;

      return {
        ...alterationRequest,
        pricing: this.#setPricingValue(alterationRequest.pricing || {}, key, 0),
      };
    });

    this.fetchQuoteSubject.next();
  }

  updatePricing(key: string, value: string) {
    if (!key) return;

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return;

    // Remove from removed items set since we're editing it
    this.removedItems.update((removed) => {
      const newSet = new Set(removed);
      newSet.delete(key);
      return newSet;
    });

    this.alterationRequest.update((alterationRequest) => {
      if (!alterationRequest) return alterationRequest;

      return {
        ...alterationRequest,
        pricing: this.#setPricingValue(alterationRequest.pricing || {}, key, numericValue),
      };
    });

    this.fetchQuoteSubject.next();
  }

  #setPricingValue(pricing: Record<string, unknown>, key: string, value: number): Record<string, unknown> {
    // Handle dot notation in the key
    const keys = key.split('.');
    const result = { ...pricing };
    let target: Record<string, unknown> = result;

    // Navigate to the parent object of the final key
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (!(k in target) || typeof target[k] !== 'object') {
        target[k] = {};
      }
      target = target[k] as Record<string, unknown>;
    }

    // Set the final value
    const finalKey = keys[keys.length - 1];
    target[finalKey] = value;

    return result;
  }

  #clearCustomPricing() {
    this.alterationRequest.update((alterationRequest) => {
      if (!alterationRequest) return alterationRequest;

      return {
        ...alterationRequest,
        pricing: {},
      };
    });
  }
}
